import * as THREE from 'three';
import { box, cyl } from '../core/meshkit.js';

// Ambient wildlife — butterflies drifting near the ground and birds gliding
// overhead. Pure decoration to make the outdoor zones feel alive. Drop one into
// any outdoor scene:
//   this.ambient = createAmbient({ center, radius, butterflies, birds, fireflies });
//   this.scene.add(this.ambient.group);   // then this.ambient.update(dt, timeOfDay) each frame

const BFLY = [0xE6537B, 0xF49B33, 0x3D5FC0, 0xF7C948, 0xB88FC4, 0xFFFFFF];

class Butterfly {
  constructor(center, radius){
    this.center = center; this.radius = radius;
    this.group = new THREE.Group();
    const col = BFLY[(Math.random() * BFLY.length) | 0];
    cyl(0.02, 0.02, 0.2, 0x2E2018, 0, 0, 0, this.group, 5);        // body
    this.wingL = new THREE.Group(); this.group.add(this.wingL);
    box(0.16, 0.012, 0.13, col, -0.09, 0, 0, this.wingL);
    this.wingR = new THREE.Group(); this.group.add(this.wingR);
    box(0.16, 0.012, 0.13, col, 0.09, 0, 0, this.wingR);

    this.t = Math.random() * 10;
    this.flap = 11 + Math.random() * 6;
    this.target = this._rand();
    this.group.position.copy(this._rand());
  }
  _rand(){
    const a = Math.random() * Math.PI * 2, r = Math.random() * this.radius;
    return new THREE.Vector3(this.center.x + Math.cos(a) * r, 0.6 + Math.random() * 1.3, this.center.z + Math.sin(a) * r);
  }
  update(dt){
    this.t += dt;
    const f = 0.15 + Math.max(0, Math.sin(this.t * this.flap)) * 1.1;   // wing flap
    this.wingL.rotation.z = f; this.wingR.rotation.z = -f;
    const to = new THREE.Vector3().subVectors(this.target, this.group.position);
    if (to.length() < 0.4) this.target = this._rand();
    to.normalize();
    this.group.position.addScaledVector(to, dt * 1.1);
    this.group.position.y += Math.sin(this.t * 3) * dt * 0.3;           // gentle bob
    this.group.rotation.y = Math.atan2(to.x, to.z);
  }
}

class Bird {
  constructor(center, radius, kind = 'bird'){
    this.center = center;
    this.kind = kind;
    this.group = new THREE.Group();
    const bodyColor = kind === 'seagull' ? 0xE8E8E8 : 0x4A4A55;
    box(0.42, 0.12, 0.13, bodyColor, 0, 0, 0, this.group);              // body
    box(0.16, 0.1, 0.1, kind === 'seagull' ? 0xFFD700 : 0x3A3A44, 0.26, 0.03, 0, this.group); // head / beak
    this.wingL = new THREE.Group(); this.group.add(this.wingL);
    box(0.5, 0.02, 0.2, kind === 'seagull' ? 0xFFFFFF : 0x53535F, 0, 0, -0.28, this.wingL);
    this.wingR = new THREE.Group(); this.group.add(this.wingR);
    box(0.5, 0.02, 0.2, kind === 'seagull' ? 0xFFFFFF : 0x53535F, 0, 0, 0.28, this.wingR);

    this.R = radius * 0.8 + Math.random() * 2;
    this.y = 6 + Math.random() * 3;
    this.angle = Math.random() * Math.PI * 2;
    this.speed = 0.22 + Math.random() * 0.15;
    this.t = Math.random() * 5; this.flap = 4 + Math.random() * 2;
  }
  update(dt){
    this.t += dt; this.angle += dt * this.speed;
    this.group.position.set(
      this.center.x + Math.cos(this.angle) * this.R,
      this.y + Math.sin(this.t) * 0.3,
      this.center.z + Math.sin(this.angle) * this.R
    );
    this.group.rotation.y = Math.atan2(-Math.sin(this.angle), Math.cos(this.angle));  // face travel
    const f = Math.sin(this.t * this.flap) * 0.5;
    this.wingL.rotation.x = f; this.wingR.rotation.x = -f;
  }
}

class Firefly {
  constructor(center, radius){
    this.center = center; this.radius = radius;
    this.group = new THREE.Group();
    const bulb = new THREE.Mesh(
      new THREE.SphereGeometry(0.035, 6, 6),
      new THREE.MeshBasicMaterial({ color: 0xCCFF66 })
    );
    this.group.add(bulb);
    this.light = new THREE.PointLight(0xCCFF66, 0.4, 2.5, 1.5);
    this.group.add(this.light);

    this.t = Math.random() * 10;
    this.target = this._rand();
    this.group.position.copy(this._rand());
  }
  _rand(){
    const a = Math.random() * Math.PI * 2, r = Math.random() * this.radius;
    return new THREE.Vector3(this.center.x + Math.cos(a) * r, 0.4 + Math.random() * 1.6, this.center.z + Math.sin(a) * r);
  }
  update(dt){
    this.t += dt;
    const to = new THREE.Vector3().subVectors(this.target, this.group.position);
    if (to.length() < 0.3) this.target = this._rand();
    to.normalize();
    this.group.position.addScaledVector(to, dt * 0.7);
    this.group.position.y += Math.sin(this.t * 2) * dt * 0.15;
    const glow = 0.5 + Math.max(0, Math.sin(this.t * 3)) * 0.5;
    this.light.intensity = glow * 0.5;
  }
}

export function createAmbient({ center = new THREE.Vector3(), radius = 16, butterflies = 4, birds = 2, fireflies = 0, seagulls = 0 } = {}){
  const group = new THREE.Group();
  const items = [];
  for (let i = 0; i < butterflies; i++){ const b = new Butterfly(center, radius); group.add(b.group); items.push({ item: b, dayOnly: true }); }
  for (let i = 0; i < birds; i++){ const b = new Bird(center, radius); group.add(b.group); items.push({ item: b, dayOnly: true }); }
  for (let i = 0; i < seagulls; i++){ const b = new Bird(center, radius, 'seagull'); group.add(b.group); items.push({ item: b, dayOnly: true }); }
  for (let i = 0; i < fireflies; i++){ const f = new Firefly(center, radius); group.add(f.group); items.push({ item: f, nightOnly: true }); }

  return {
    group,
    update(dt, timeOfDay = 'day'){
      const isNight = timeOfDay === 'night';
      for (const { item, dayOnly, nightOnly } of items){
        if (dayOnly && isNight){
          item.group.visible = false;
          continue;
        }
        if (nightOnly && !isNight){
          item.group.visible = false;
          continue;
        }
        item.group.visible = true;
        item.update(dt);
      }
    }
  };
}
